import { Phone, MapPin, Car, Zap, Navigation, Pencil, Route, Shuffle, X, Undo2, CalendarClock } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  STATUS_COLORS,
  PRIORITY_COLORS,
  SERVICE_LABEL,
  STATUS_LABEL,
} from "../../lib/constants";
import { formatDistance, formatDuration, cn } from "../../lib/utils";

// Prefer coords (no geocoding mismatch on the driver's phone), fall back to address text.
// Universal Google Maps directions link — works on iOS, Android, and desktop.
export const mapsLink = (addr, lat, lng) => {
  if (typeof lat === "number" && typeof lng === "number" && (lat !== 0 || lng !== 0)) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}`;
};

export const telLink = (phone) => `tel:${(phone || "").replace(/[^+\d]/g, "")}`;

// ─────────────────────────────────────────────────────────────────────────────
// Badges row: priority pill, status pill, source-specific WO/AA badges.
// ─────────────────────────────────────────────────────────────────────────────
export function JobCardBadges({ job, isAaPlaceholder }) {
  const status = STATUS_COLORS[job.status] || STATUS_COLORS.pending;
  const prio = PRIORITY_COLORS[job.priority] || PRIORITY_COLORS.normal;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span
        className={cn(
          "inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wide",
          prio.bg,
          prio.text
        )}
        data-testid={`job-priority-${job.id}`}
      >
        {job.priority === "urgent" && <Zap className="h-3 w-3" strokeWidth={3} />}
        {job.priority}
      </span>
      <Badge
        variant="secondary"
        className={cn("font-semibold", status.bg, status.text, "border-0")}
        data-testid={`job-status-${job.id}`}
      >
        <span className={cn("h-1.5 w-1.5 rounded-full mr-1.5", status.dot)} />
        {STATUS_LABEL[job.status]}
      </Badge>
      {(job.source === "servicenow_image" || job.source === "servicenow_api") && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-900 bg-violet-200 px-1.5 py-0.5 rounded"
          data-testid={`job-sn-id-${job.id}`}
        >
          {job.work_order_number ? `FR · ${job.work_order_number}` : "First Rescue"}
        </span>
      )}
      {job.source === "aa_sms" && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded"
          data-testid={`job-aa-badge-${job.id}`}
        >
          AA · {job.aa_job_id || "SMS"}
        </span>
      )}
      {job.source === "aa_workforms" && job.aa_job_id && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded"
          data-testid={`job-aa-id-${job.id}`}
        >
          AA · {job.aa_job_id}
        </span>
      )}
      {isAaPlaceholder && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded animate-pulse"
          data-testid={`job-needs-details-${job.id}`}
        >
          Needs Details
        </span>
      )}
      {/* AA cancel-on-route awaiting dispatcher decision. Piggybacks the
          job card's rose-500 outer pulse; this badge shouts what the pulse
          means so a fresh dispatcher understands at a glance. */}
      {job.charge_decision_needed && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-rose-600 px-1.5 py-0.5 rounded"
          data-testid={`job-decide-charge-${job.id}`}
          title="AA cancelled this job while the driver was on the way — decide Charge or No Charge"
        >
          <X className="h-3 w-3" strokeWidth={3} />
          Cancelled by AA — Decide Charge
        </span>
      )}
      {/*
        Yard-outbound auto-link ribbon (Feb 2026): when a fresh AA / FR /
        email / retro job is detected as a release for a vehicle sitting
        in one of our yards, we tag it here so the dispatcher can see at
        a glance that assigning + on-siting this job will stop the linked
        storage clock.
      */}
      {job.linked_yard_job_id && (
        <span
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-900 bg-emerald-100 px-1.5 py-0.5 rounded"
          data-testid={`job-yard-release-${job.id}`}
          title="Storage clock stops when this truck arrives on-site at the yard"
        >
          <Undo2 className="h-3 w-3" strokeWidth={2.5} />
          Yard release
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Body: pickup link, drop-off link, route ETA, vehicle, phone, claim/program.
// ─────────────────────────────────────────────────────────────────────────────
export function JobCardBody({ job, dense }) {
  return (
    <div className={cn("mt-2 text-slate-600", dense ? "space-y-0.5 text-xs" : "space-y-1 text-sm")}>
      <div className="flex items-start gap-2">
        <MapPin className="h-3.5 w-3.5 mt-0.5 text-slate-400 shrink-0" />
        {job.address ? (
          <a
            href={mapsLink(job.address, job.lat, job.lng)}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-slate-700 hover:text-blue-700 hover:underline inline-flex items-center gap-1"
            data-testid={`job-pickup-link-${job.id}`}
          >
            <span className="truncate">{job.address}</span>
            <Navigation className="h-3 w-3 shrink-0" />
          </a>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </div>
      {job.drop_off_address && (
        <div className="flex items-start gap-2">
          <MapPin className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
          <a
            href={mapsLink(job.drop_off_address, job.drop_off_lat, job.drop_off_lng)}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-slate-700 hover:text-emerald-700 hover:underline inline-flex items-center gap-1"
            data-testid={`job-dropoff-link-${job.id}`}
          >
            <span className="truncate">Drop-off: {job.drop_off_address}</span>
            <Navigation className="h-3 w-3 shrink-0" />
          </a>
        </div>
      )}
      {job.route_duration_s != null && job.route_distance_m != null && (
        <div
          className="flex items-center gap-1.5 pl-5 text-[11px] font-semibold text-slate-700"
          data-testid={`job-route-eta-${job.id}`}
        >
          <Route className="h-3 w-3 text-blue-600 shrink-0" />
          <span className="text-blue-700">{formatDuration(job.route_duration_s)}</span>
          <span className="text-slate-300">·</span>
          <span className="font-mono text-slate-500">{formatDistance(job.route_distance_m)}</span>
          <span className="text-slate-400 font-normal">to drop-off</span>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Car className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        <span className="truncate">{job.vehicle}</span>
        {job.rego && (
          <span className="font-mono text-[11px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
            {job.rego}
          </span>
        )}
        <span className="text-slate-400">·</span>
        <span className="font-semibold text-slate-700">{SERVICE_LABEL[job.service_type]}</span>
      </div>
      {job.customer_phone && (
        <div className="flex items-center gap-2">
          <Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <a
            href={telLink(job.customer_phone)}
            className="text-slate-700 hover:text-blue-700 hover:underline"
            data-testid={`job-phone-link-${job.id}`}
          >
            {job.customer_phone}
          </a>
        </div>
      )}
      {(job.claim_number || job.program) && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {job.program && <span className="font-semibold text-violet-700">{job.program}</span>}
          {job.claim_number && (
            <>
              {job.program && <span className="text-slate-300">·</span>}
              <span className="font-mono">Claim {job.claim_number}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Partner-source jobs are urgent same-day by contract — hide "Later"
// on them. Mirrors `_BOOK_FOR_LATER_BLOCKED_SOURCES` on the backend.
const BOOK_FOR_LATER_BLOCKED_SOURCES = new Set([
  "aa_workforms", "aa_sms",
  "servicenow", "servicenow_api", "servicenow_image",
]);

// ─────────────────────────────────────────────────────────────────────────────
// Action button row: fill-details / assign / advance / reassign / edit / cancel.
// ─────────────────────────────────────────────────────────────────────────────
export function JobCardActions({ job, next, isAaPlaceholder, dense, onAssign, onAdvance, onCancel, onEdit, onCancelOnRoute, onBookForLater }) {
  // "Later" button — pending, non-partner, no placeholder, no charge decision.
  // See `/app/backend/routes/jobs.py::book_pending_job_for_later` for the
  // mirror server-side gate; both must agree or the button will 400.
  const canBookForLater =
    typeof onBookForLater === "function" &&
    job.status === "pending" &&
    !isAaPlaceholder &&
    !job.charge_decision_needed &&
    !BOOK_FOR_LATER_BLOCKED_SOURCES.has(job.source);
  // Stop click events bubbling to the parent card (which may have an
  // onCardClick handler that opens the detail sheet). Without this,
  // pressing Assign / Edit / Advance / Cancel would also open the sheet.
  const stopBubble = (e) => e.stopPropagation();
  return (
    <div
      className={cn(
        "flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100",
        dense && "mt-auto gap-1.5 pt-2 [&_button]:h-7 [&_button]:px-2 [&_button]:text-[11px]",
      )}
      onClick={stopBubble}
    >
      {isAaPlaceholder && onEdit && (
        <Button
          size="sm"
          onClick={() => onEdit(job)}
          className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
          data-testid={`job-fill-details-btn-${job.id}`}
        >
          <Pencil className="h-3.5 w-3.5" />
          Fill Details
        </Button>
      )}
      {job.status === "pending" && (
        <Button
          size="sm"
          onClick={() => onAssign?.(job)}
          disabled={isAaPlaceholder}
          className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
          data-testid={`job-assign-btn-${job.id}`}
        >
          Assign Truck
        </Button>
      )}
      {next && job.status !== "pending" && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onAdvance?.(job, next.value)}
          className="hover:bg-slate-200 hover:border-slate-400 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-50"
          data-testid={`job-advance-btn-${job.id}`}
        >
          Mark {next.label}
        </Button>
      )}
      {job.assigned_truck_id && !["completed", "cancelled", "awaiting_partner_close"].includes(job.status) && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onAssign?.(job)}
          className="hover:bg-slate-200 hover:border-slate-400 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-50"
          data-testid={`job-reassign-btn-${job.id}`}
        >
          <Shuffle className="h-3.5 w-3.5" />
          Reassign
        </Button>
      )}
      {!isAaPlaceholder && onEdit && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEdit(job)}
          className="hover:bg-slate-200 hover:border-slate-400 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-50"
          data-testid={`job-edit-btn-${job.id}`}
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </Button>
      )}
      {canBookForLater && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onBookForLater(job)}
          className="ml-auto border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-950/30 dark:hover:text-indigo-200"
          data-testid={`job-book-for-later-btn-${job.id}`}
          title="Schedule this job for a later date/time"
        >
          <CalendarClock className="h-3.5 w-3.5" />
          Later
        </Button>
      )}
      {job.status !== "completed" && job.status !== "cancelled" && (
        <Button
          size="sm"
          variant="ghost"
          className={cn(
            "text-slate-500 hover:text-rose-700",
            // "Later" owns `ml-auto` when visible; otherwise Cancel keeps it so
            // the action row still right-aligns the destructive button.
            !canBookForLater && "ml-auto",
          )}
          onClick={() => onCancel?.(job)}
          data-testid={`job-cancel-btn-${job.id}`}
        >
          Cancel
        </Button>
      )}
      {/* Customer cancelled mid-travel — dispatcher records Charge / No Charge.
          When the AA cancel-detector has already flagged this job the button
          is upgraded to a filled variant to match the pulse ring so the CTA
          is obvious. Also shown regardless of `assigned_truck_id` state
          (the driver ack clears it) as long as the flag is live. */}
      {onCancelOnRoute && (
        job.charge_decision_needed ||
        ["assigned", "accepted", "en_route", "on_site", "awaiting_partner_close"].includes(job.status)
      ) && (
        <Button
          size="sm"
          variant={job.charge_decision_needed ? "default" : "outline"}
          className={cn(
            "rounded-full px-3 h-7 text-xs font-bold",
            job.charge_decision_needed
              ? "bg-rose-600 hover:bg-rose-700 text-white border-rose-700"
              : "border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950/30",
          )}
          onClick={() => onCancelOnRoute(job)}
          data-testid={`job-cancel-on-route-btn-${job.id}`}
        >
          <X className="h-3 w-3" strokeWidth={3} />
          {job.charge_decision_needed ? "Decide Charge" : "Cancelled en Route"}
        </Button>
      )}
    </div>
  );
}
